// src/pages/PainelChamados/Menu.jsx
import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { Save, XCircle, CheckCircle, UserPlus } from "lucide-react";
import toast from "react-hot-toast"; // ✅ react-hot-toast
import ModalAtribuir from "./ModalAtribuir";

const API_URL = process.env.REACT_APP_API_URL;

export default function Menu({
  onCancelar,
  colaborador,
  tecnicoResponsavel,
  relatoProblema,
  solucaoProblema,
  filtroCliente,
  status,
  setStatus,
  tipoServico,
  finalizadoEm,
  setFinalizadoEm,
  onSalvo,
}) {
  const [open, setOpen] = useState(false);
  const [modalAtribuirOpen, setModalAtribuirOpen] = useState(false);

  const buttonStyle =
    "w-32 py-2 rounded-md text-white font-semibold shadow-md mx-auto flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-transform duration-200";

  const options = ["Finalizado", "Pendente", "Aberto", "Atendendo"];
  const statusColor = {
    Finalizado: "text-green-600",
    Pendente: "text-red-600",
    Aberto: "text-yellow-500",
    Atendendo: "text-blue-600",
  };

  // Função genérica que retorna a Promise do fetch
  const salvarChamado = (dados) =>
    fetch(`${API_URL}/chamados/inserir.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(dados),
    }).then(async (resp) => {
      const data = await resp.json();
      if (!data.sucesso) throw new Error("Erro ao salvar chamado");
      return data;
    });

  // Salvar normal
  const handleSalvar = () => {
    toast.promise(
      salvarChamado({
        empresa: filtroCliente,
        colaborador,
        tecnico_responsavel: tecnicoResponsavel,
        relato_problema: relatoProblema,
        solucao_problema: solucaoProblema,
        status: status || "Aberto",
        tipo_servico: tipoServico,
        finalizado_em: finalizadoEm || null,
      }),
      {
        loading: "💾 Salvando chamado...",
        success: () => {
          if (onSalvo) onSalvo();
          return "✅ Chamado salvo com sucesso!";
        },
        error: "❌ Erro ao salvar chamado!",
      }
    );
  };

  // Finalizar já mandando finalizado_em
  const handleFinalizar = () => {
    const agora = new Date().toISOString().slice(0, 19).replace("T", " ");
    setFinalizadoEm(agora);

    toast.promise(
      salvarChamado({
        empresa: filtroCliente,
        colaborador,
        tecnico_responsavel: tecnicoResponsavel,
        relato_problema: relatoProblema,
        solucao_problema: solucaoProblema,
        status: "Finalizado",
        tipo_servico: tipoServico,
        finalizado_em: agora,
      }),
      {
        loading: "⏳ Finalizando chamado...",
        success: () => {
          if (onSalvo) onSalvo();
          return "✅ Chamado finalizado!";
        },
        error: "❌ Erro ao finalizar chamado!",
      }
    );
  };

  // Confirmar atribuição
  const handleConfirmarAtribuicao = ({ quemAtribui, quemRecebe }) => {
    console.log("Chamado atribuído:", quemAtribui, "→", quemRecebe);
    toast(`📌 Chamado atribuído de ${quemAtribui} para ${quemRecebe}`, {
      icon: "👤",
    });
    setModalAtribuirOpen(false);
  };

  return (
    <>
      <motion.div
        initial={{ x: 50, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: 50, opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="flex flex-col gap-3 items-center justify-between h-full"
      >
        {/* Botões */}
        <div className="flex flex-col gap-3 items-center">
          <button
            onClick={handleSalvar}
            className={`${buttonStyle} hover:scale-105`}
            style={{ backgroundColor: "#16a34a" }}
          >
            <Save size={16} /> Salvar
          </button>

          <button
            onClick={onCancelar}
            className={`${buttonStyle} hover:scale-105`}
            style={{ backgroundColor: "#b50000" }}
          >
            <XCircle size={16} /> Cancelar
          </button>

          <button
            onClick={handleFinalizar}
            className={`${buttonStyle} hover:scale-105`}
            style={{ backgroundColor: "#1e7f14" }}
          >
            <CheckCircle size={16} /> Finalizar
          </button>

          <button
            onClick={() => setModalAtribuirOpen(true)}
            className={`${buttonStyle} hover:scale-105`}
            style={{ backgroundColor: "#2563eb" }}
          >
            <UserPlus size={16} /> Atribuir
          </button>
        </div>

        {/* Status select */}
        <div className="w-full mt-4 relative">
          <div
            onClick={() => setOpen((prev) => !prev)}
            className={`w-full border-0 border-b border-gray-400 px-1 py-2 text-lg cursor-pointer bg-transparent text-center font-bold ${
              status ? statusColor[status] : "text-gray-600"
            }`}
          >
            {status || "Status"}
          </div>

          <AnimatePresence>
            {open && (
              <motion.ul
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.2 }}
                className="absolute left-0 right-0 bottom-full bg-white border border-gray-300 rounded-md shadow-md z-10"
              >
                {options.map((opt) => (
                  <li
                    key={opt}
                    onClick={() => {
                      setStatus(opt);
                      setOpen(false);
                    }}
                    className={`px-3 py-2 text-sm hover:bg-blue-100 cursor-pointer ${
                      status === opt ? "font-semibold text-blue-600" : ""
                    }`}
                  >
                    {opt}
                  </li>
                ))}
              </motion.ul>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Modal Atribuir */}
      <ModalAtribuir
        open={modalAtribuirOpen}
        onClose={() => setModalAtribuirOpen(false)}
        onConfirm={handleConfirmarAtribuicao}
        tecnicoResponsavel={tecnicoResponsavel}
      />
    </>
  );
}
